import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './Comenzi.css'

const GET_CLIENTS_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/getClients'
const GET_CLIENT_DETAILS_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/getClientDetails'
const GET_ORDER_STATUS_HISTORY_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/getOrderStatusHistory'

const ORDER_STATUSES = [
    'solicitare',
    'ofertat',
    'acceptat',
    'asteptare_plata',
    'comanda_furnizor',
    'pregatire_colet',
    'expediat',
    'finalizat',
    'refuzat'
]

const ORDER_STATUS_LABELS = {
    solicitare: 'Solicitare',
    ofertat: 'Ofertat',
    acceptat: 'Acceptat',
    asteptare_plata: 'Așteptare plată',
    comanda_furnizor: 'Comandă furnizor',
    pregatire_colet: 'Pregătire colet',
    expediat: 'Expediat',
    finalizat: 'Finalizat',
    refuzat: 'Refuzat'
}

function Comenzi() {
    const navigate = useNavigate()

    const [orders, setOrders] = useState([])
    const [activeFilter, setActiveFilter] = useState('toate')
    const [orderSearch, setOrderSearch] = useState('')
    const [dateRange, setDateRange] = useState({ start: '', end: '' })
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [calendarMonth, setCalendarMonth] = useState(new Date())
    const [loading, setLoading] = useState(true)
    const [loadingNote, setLoadingNote] = useState('')
    const [error, setError] = useState('')

    useEffect(() => {
        getOrdersFromClients()
    }, [])
    async function getOrderHistoryDates(order) {
        const uuid = getOrderUuid(order)

        if (!uuid) {
            return {
                latest_status_date: '',
                solicitare_status_date: ''
            }
        }

        try {
            const response = await fetch(
                `${GET_ORDER_STATUS_HISTORY_API_URL}?uuid=${encodeURIComponent(uuid)}`,
                {
                    method: 'GET'
                }
            )

            const data = await response.json()

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Nu am putut încărca istoricul.')
            }

            const history = Array.isArray(data.history) ? data.history : []

            const solicitareHistory = history.find(
                (historyItem) => historyItem.status === 'solicitare'
            )

            const currentStatusHistory = [...history]
                .reverse()
                .find(
                    (historyItem) =>
                        historyItem.status === data.currentStatus
                )

            const latestHistory = [...history].sort(
                (firstItem, secondItem) =>
                    new Date(secondItem.created_at).getTime() -
                    new Date(firstItem.created_at).getTime()
            )[0]

            return {
                latest_status_date:
                    currentStatusHistory?.created_at ||
                    latestHistory?.created_at ||
                    '',
                solicitare_status_date:
                    solicitareHistory?.created_at ||
                    order.solicitare_data ||
                    ''
            }
        } catch (err) {
            console.error('GET ORDER HISTORY DATES ERROR:', err)

            return {
                latest_status_date: '',
                solicitare_status_date: order.solicitare_data || ''
            }
        }
    }
    async function getOrdersFromClients() {
        try {
            setLoading(true)
            setError('')
            setLoadingNote('Se încarcă lista de clienți...')

            const clientsResponse = await fetch(GET_CLIENTS_API_URL, {
                method: 'GET'
            })

            const clientsText = await clientsResponse.text()

            if (!clientsResponse.ok) {
                throw new Error(`Get clients failed: ${clientsResponse.status}`)
            }

            const clientsData = JSON.parse(clientsText)

            const clientsList = Array.isArray(clientsData)
                ? clientsData
                : clientsData.clients || clientsData.clienti || []

            setLoadingNote(`Se încarcă comenzile`)

            const clientDetailsResults = await Promise.allSettled(
                clientsList.map(async (clientItem) => {
                    const clientSlug = getClientSlug(clientItem)

                    if (!clientSlug) {
                        return null
                    }

                    const url = `${GET_CLIENT_DETAILS_API_URL}?slug=${encodeURIComponent(clientSlug)}`

                    const response = await fetch(url, {
                        method: 'GET'
                    })

                    const text = await response.text()

                    if (!response.ok) {
                        throw new Error(`Get client details failed for ${clientSlug}: ${response.status}`)
                    }

                    const data = JSON.parse(text)

                    if (!data.success) {
                        throw new Error(data.message || `Client details failed for ${clientSlug}`)
                    }

                    const client = {
                        ...clientItem,
                        ...(data.client || {}),
                        contacte: data.contacte || [],
                        adrese: data.adrese || []
                    }

                    const clientName = getClientName(client)
                    const clientLogo = getClientLogo(client)
                    const finalClientSlug = getClientSlug(client) || clientSlug

                    const clientOrders = data.oferte || []

                    const ordersWithHistory = await Promise.all(
                        clientOrders.map(async (offer) => {
                            const historyDates = await getOrderHistoryDates(offer)

                            return {
                                ...offer,
                                ...historyDates,
                                clientId: client.id,
                                clientSlug: finalClientSlug,
                                client_nume: clientName,
                                client_logo: clientLogo
                            }
                        })
                    )

                    return ordersWithHistory
                })
            )

            const allOrders = clientDetailsResults
                .filter((result) => result.status === 'fulfilled' && Array.isArray(result.value))
                .flatMap((result) => result.value)
                .sort((a, b) => {
                    const firstDate = new Date(getOrderDate(b)).getTime() || 0
                    const secondDate = new Date(getOrderDate(a)).getTime() || 0

                    return firstDate - secondDate
                })

            setOrders(allOrders)
        } catch (err) {
            console.error('GET ORDERS FROM CLIENTS ERROR:', err)
            setError('Nu am putut încărca lista de comenzi.')
        } finally {
            setLoading(false)
            setLoadingNote('')
        }
    }

    function getInitials(name) {
        return String(name || '?')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((word) => word.charAt(0))
            .join('')
            .toUpperCase()
    }

    function createClientSlug(name) {
        return String(name || '')
            .trim()
            .replace(/\s+/g, '-')
    }

    function getClientSlug(client) {
        return client.slug || client.clientSlug || client.client_slug || createClientSlug(client.nume || client.name || client.client_nume)
    }

    function getClientName(client) {
        return client.client_nume || client.clientNume || client.client_name || client.nume_client || client.nume || client.name || client.client || '-'
    }

    function getClientLogo(client) {
        return client.client_logo || client.clientLogo || client.logo_client || client.logo || ''
    }

    function getOrderName(order) {
        return order.nume_comanda || order.numeComanda || order.name || order.titlu || '-'
    }
    function getOrderNumber(order) {
        const id = Number(order.id)

        if (!Number.isFinite(id)) {
            return '-'
        }

        return 1000 + id
    }
    function getOrderDate(order) {
        return order.solicitare_data || order.dateOrder || order.data || order.created_at || order.createdAt || ''
    }

    function getOrderUuid(order) {
        return order.uuid || order.order_uuid || order.orderUuid || order.id
    }

   function formatDate(dateString) {
    if (!dateString) {
        return '-'
    }

    const cleanDate = String(dateString)
        .replace('T', ' ')
        .replace('.000Z', '')
        .replace('Z', '')

    const [datePart, timePart = '00:00:00'] = cleanDate.split(' ')
    const [year, month, day] = datePart.split('-')
    const [hour = '00', minute = '00'] = timePart.split(':')

    return `${day}.${month}.${year} • ${hour}.${minute}`
}
    function getOrderDateForFilter(order) {
        const value = getOrderDate(order)

        if (!value) {
            return ''
        }

        const date = new Date(value)

        if (Number.isNaN(date.getTime())) {
            return String(value).slice(0, 10)
        }

        return date.toISOString().slice(0, 10)
    }

    function updateDateRange(value) {
        if (!dateRange.start || dateRange.end) {
            setDateRange({ start: value, end: '' })
            return
        }

        if (value < dateRange.start) {
            setDateRange({ start: value, end: dateRange.start })
            setIsDatePickerOpen(false)
            return
        }

        setDateRange({ start: dateRange.start, end: value })
        setIsDatePickerOpen(false)
    }

    function clearDateRange() {
        setDateRange({ start: '', end: '' })
    }

    function getDateRangeLabel() {
        if (dateRange.start && dateRange.end) {
            return `${dateRange.start} → ${dateRange.end}`
        }

        if (dateRange.start) {
            return `${dateRange.start} → alege final`
        }

        return 'Calendar'
    }

    function openOrder(order) {
        const clientSlug = order.clientSlug || order.client_slug || createClientSlug(getClientName(order))
        const orderUuid = getOrderUuid(order)

        if (!clientSlug || !orderUuid) {
            return
        }

        navigate(`/clienti/${clientSlug}/${orderUuid}`)
    }

    const filteredOrders = useMemo(() => {
        const searchValue = orderSearch.trim().toLowerCase()

        return orders.filter((order) => {
            const status = order.status || ''

            const matchesStatus =
                activeFilter === 'toate' ||
                (activeFilter === 'arhivate' && ['finalizat', 'refuzat'].includes(status)) ||
                status === activeFilter

            const matchesSearch =
                !searchValue ||
                [
                    getClientName(order),
                    getOrderName(order),
                    getOrderNumber(order),
                    status,
                    ORDER_STATUS_LABELS[status]
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(searchValue)

            const orderDate = getOrderDateForFilter(order)

            const matchesDate =
                (!dateRange.start || orderDate >= dateRange.start) &&
                (!dateRange.end || orderDate <= dateRange.end)

            return matchesStatus && matchesSearch && matchesDate
        })
    }, [orders, activeFilter, orderSearch, dateRange])

    const calendarYear = calendarMonth.getFullYear()
    const calendarMonthIndex = calendarMonth.getMonth()
    const firstDayOfMonth = new Date(calendarYear, calendarMonthIndex, 1)
    const lastDayOfMonth = new Date(calendarYear, calendarMonthIndex + 1, 0)
    const startWeekDay = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1
    const daysInMonth = lastDayOfMonth.getDate()

    const calendarDays = [
        ...Array.from({ length: startWeekDay }, () => null),
        ...Array.from({ length: daysInMonth }, (_, index) => {
            const date = new Date(calendarYear, calendarMonthIndex, index + 1)
            return date.toISOString().slice(0, 10)
        })
    ]

    if (loading) {
        return (
            <section className="comenziPage">
                <div className="comenziLoading">
                    <strong>Se încarcă comenzile...</strong>
                    <span>{loadingNote}</span>
                </div>
            </section>
        )
    }

    if (error) {
        return <section className="comenziPage">{error}</section>
    }

    return (
        <section className="comenziPage">
            <div className="comenziTop">
                <div>
                    <h1>Comenzi</h1>

                </div>
            </div>

            <div className="offersFilterHeader">
                <span>
                    <strong>Filtrează după status</strong> sau
                </span>

                <div className="offersSearchInput">
                    <span>⌕</span>

                    <input
                        type="text"
                        placeholder="Caută comandă sau client..."
                        value={orderSearch}
                        onChange={(event) => setOrderSearch(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                setOrderSearch('')
                            }
                        }}
                    />

                    {orderSearch && (
                        <button
                            type="button"
                            className="searchClearButton"
                            onClick={() => setOrderSearch('')}
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="offersDateFilter">
                    <button
                        type="button"
                        className="offersDateButton"
                        onClick={() => setIsDatePickerOpen((prev) => !prev)}
                    >
                        {getDateRangeLabel()}
                    </button>

                    {isDatePickerOpen && (
                        <div className="offersCalendarPopover">
                            <div className="offersCalendarHeader">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setCalendarMonth(
                                            new Date(calendarYear, calendarMonthIndex - 1, 1)
                                        )
                                    }
                                >
                                    ‹
                                </button>

                                <strong>
                                    {calendarMonth.toLocaleDateString('ro-RO', {
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </strong>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setCalendarMonth(
                                            new Date(calendarYear, calendarMonthIndex + 1, 1)
                                        )
                                    }
                                >
                                    ›
                                </button>
                            </div>

                            <div className="offersCalendarWeekdays">
                                <span>L</span>
                                <span>M</span>
                                <span>M</span>
                                <span>J</span>
                                <span>V</span>
                                <span>S</span>
                                <span>D</span>
                            </div>

                            <div className="offersCalendarGrid">
                                {calendarDays.map((day, index) => {
                                    if (!day) {
                                        return <span key={`empty-${index}`} />
                                    }

                                    const isStart = dateRange.start === day
                                    const isEnd = dateRange.end === day
                                    const isInside =
                                        dateRange.start &&
                                        dateRange.end &&
                                        day > dateRange.start &&
                                        day < dateRange.end

                                    return (
                                        <button
                                            type="button"
                                            key={day}
                                            className={[
                                                isStart ? 'rangeStart' : '',
                                                isEnd ? 'rangeEnd' : '',
                                                isInside ? 'rangeInside' : ''
                                            ].join(' ')}
                                            onClick={() => updateDateRange(day)}
                                        >
                                            {new Date(day).getDate()}
                                        </button>
                                    )
                                })}
                            </div>

                            <button
                                type="button"
                                className="offersCalendarClear"
                                onClick={clearDateRange}
                            >
                                Șterge perioada
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="offersToolbar">
                {[
                    ['toate', 'Toate'],
                    ...ORDER_STATUSES.map((status) => [status, ORDER_STATUS_LABELS[status]])
                ].map(([value, label]) => (
                    <button
                        type="button"
                        key={value}
                        className={activeFilter === value ? 'active' : ''}
                        onClick={() => setActiveFilter(value)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="offersTable comenziTable">
                <div className="offersTableHead comenziTableHead">
                    <span>Client</span>
                    <span>Nume comandă</span>
                    <span>ID comandă</span>
                    <span>Data și oră Status</span>
                    <span>Status</span>
                    <span></span>
                </div>

                {filteredOrders.length === 0 && (
                    <div className="emptyOffers">
                        Nu există comenzi pentru filtrele selectate.
                    </div>
                )}

                {filteredOrders.map((order) => {
                    const clientName = getClientName(order)
                    const clientLogo = getClientLogo(order)
                    const orderName = getOrderName(order)
                    const orderUuid = getOrderUuid(order)

                    return (
                        <div
                            className="offerRow comenziRow"
                            key={`${order.clientSlug || order.client_slug || clientName}-${orderUuid || order.id}`}
                            onClick={() => openOrder(order)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    openOrder(order)
                                }
                            }}
                        >
                            <div className="comenziClientCell">
                                <div className="comenziClientLogo">
                                    {clientLogo ? (
                                        <img src={clientLogo} alt={clientName} />
                                    ) : (
                                        <span>{getInitials(clientName)}</span>
                                    )}
                                </div>

                                <strong>{clientName}</strong>
                            </div>

                            <span className="comenziOfferName">📄 {orderName}</span>
                            <span className="comenziOrderId">
                                #{getOrderNumber(order)}
                            </span>
                            <div className="comenziStatusDates">
                                <strong>
                                    {formatDate(order.latest_status_date)}
                                </strong>

                                <small>
                                    Solicitare: {formatDate(order.solicitare_status_date)}
                                </small>
                            </div>

                            <div className={`statusPill status-${order.status}`}>
                                <span>{ORDER_STATUS_LABELS[order.status] || order.status || '-'}</span>
                            </div>

                            <span className="comenziArrow">›</span>
                        </div>
                    )
                })}
            </div>
        </section>
    )
}

export default Comenzi
